import { useState, useEffect } from 'react';
import { getOrdersDateWise } from '@/services/reportsService';
import { toastMessage } from '@/utils/toast-message';
import { TrendingUp } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface DateWiseData {
  period_start: string;
  order_count: number;
  total_revenue: number;
  paid_revenue: number;
  paid_orders: number;
}

const DateWiseReport = () => {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [period, setPeriod] = useState('daily');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DateWiseData[]>([]);

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      toastMessage('Please select both dates', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await getOrdersDateWise({
        from_date: fromDate,
        to_date: toDate,
        period,
      });

      if (response.success) {
        setData(response.data as DateWiseData[]);
      } else {
        toastMessage('Failed to fetch report', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Error fetching report', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const chartData = {
    labels: data.map(d => new Date(d.period_start).toLocaleDateString('en-IN')),
    datasets: [
      {
        label: 'Order Count',
        data: data.map(d => d.order_count),
        borderColor: '#3b82f6',
        backgroundColor: '#3b82f640',
        yAxisID: 'y',
        tension: 0.4,
      },
      {
        label: 'Total Revenue (₹)',
        data: data.map(d => d.total_revenue),
        backgroundColor: '#10b98140',
        yAxisID: 'y1',
      },
    ],
  };

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4">
          {/* Left Content */}
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="p-2 bg-brandColor rounded-lg text-white">
                <TrendingUp className="h-5 w-5" />
              </div>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Date-wise Trend</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Orders trend over time</p>
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={fetchReport}
              disabled={loading}
              className="px-3 py-1.5 bg-brandColor text-white text-sm rounded-lg hover:bg-brandColor/90 disabled:opacity-50 font-medium transition-all"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Explanation */}
        <div className="flex gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <svg className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-0.5">About this report</p>
            <p className="text-blue-700 dark:text-blue-400 leading-relaxed">
              Tracks order volume and revenue trends over time. Use the <strong>Daily / Weekly / Monthly</strong> toggle to zoom in or out on the trend.
              The dual-axis chart shows order count (left axis) and revenue (right axis) together — helping you spot periods where high order volume didn't translate to proportional revenue.
            </p>
          </div>
        </div>
        {data.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Orders & Revenue Trend</h2>
            <div className="h-96">
              <Line
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { mode: 'index', intersect: false },
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: { usePointStyle: true, padding: 15 },
                    },
                  },
                  scales: {
                    y: {
                      type: 'linear',
                      display: true,
                      position: 'left',
                      title: { display: true, text: 'Order Count' },
                    },
                    y1: {
                      type: 'linear',
                      display: true,
                      position: 'right',
                      title: { display: true, text: 'Revenue (₹)' },
                      grid: { drawOnChartArea: false },
                    },
                  },
                }}
              />
            </div>
          </div>
        )}

        {data.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Detailed Data</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Period</th>
                    <th className="px-6 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Orders</th>
                    <th className="px-6 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Total Revenue</th>
                    <th className="px-6 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Paid Revenue</th>
                    <th className="px-6 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Paid Orders</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {data.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 text-gray-900 dark:text-gray-100">
                        {new Date(row.period_start).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">{row.order_count}</td>
                      <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">
                        ₹{row.total_revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">
                        ₹{row.paid_revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900 dark:text-gray-100">{row.paid_orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brandColor"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading report...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateWiseReport;
